import React, { useState, useEffect } from 'react';
import { BarChart2, TrendingUp, AlertTriangle, Pin } from 'lucide-react';
import api from '../../api/client';

function StatCard({ label, value, subtitle, color }) {
  return (
    <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 flex-1 min-w-[140px]">
      <div className="text-2xl font-bold" style={{ color: color || '#f5f0e8' }}>{value}</div>
      <div className="text-xs text-gray-400 mt-1">{label}</div>
      {subtitle && <div className="text-[10px] text-gray-500 mt-0.5">{subtitle}</div>}
    </div>
  );
}

function rateColor(rate) {
  if (rate === null || rate === undefined) return '#6b7280';
  if (rate >= 85) return '#16a34a';
  if (rate >= 70) return '#c9a84c';
  return '#dc2626';
}

function chitchatColor(rate) {
  if (rate === null) return '#6b7280';
  if (rate < 10) return '#16a34a';
  if (rate <= 20) return '#c9a84c';
  return '#dc2626';
}

export default function AIQualityDashboard() {
  const [overview, setOverview] = useState(null);
  const [intents, setIntents] = useState([]);
  const [fields, setFields] = useState([]);
  const [weeks, setWeeks] = useState([]);
  const [pinnedStats, setPinnedStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/admin/ai-quality/overview').then(r => setOverview(r.data)),
      api.get('/admin/ai-quality/by-intent').then(r => setIntents(r.data.intents || [])),
      api.get('/admin/ai-quality/corrected-fields').then(r => setFields(r.data.fields || [])),
      api.get('/admin/ai-quality/weekly-trend').then(r => setWeeks(r.data.weeks || [])),
      api.get('/admin/ai-quality/pinned-stats').then(r => setPinnedStats(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-6 text-gray-400">Loading AI Quality data...</div>;

  const maxFieldCount = Math.max(...fields.map(f => parseInt(f.correction_count) || 0), 1);
  const maxWeeklyCount = Math.max(...weeks.map(w => parseInt(w.confirmed || 0) + parseInt(w.cancelled || 0)), 1);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <BarChart2 className="h-6 w-6 text-purple-400" />
        <h1 className="text-xl font-bold text-white">AI Quality Dashboard</h1>
        <span className="text-xs text-gray-500 ml-auto">Last 30 days</span>
      </div>

      {/* Stat Cards */}
      <div className="flex gap-3 mb-6 flex-wrap">
        <StatCard label="Total Queries" value={overview?.total_queries || 0} color="#f5f0e8" />
        <StatCard label="Confirm Rate" value={overview?.confirm_rate !== null ? `${overview.confirm_rate}%` : '—'}
          color={rateColor(overview?.confirm_rate)} subtitle="confirmed / (confirmed + cancelled)" />
        <StatCard label="Cancel Rate" value={overview?.cancel_rate !== null ? `${overview.cancel_rate}%` : '—'}
          color={overview?.cancel_rate > 30 ? '#dc2626' : '#c9a84c'} />
        <StatCard label="Chitchat Rate" value={overview?.chitchat_rate !== null ? `${overview.chitchat_rate}%` : '—'}
          color={chitchatColor(overview?.chitchat_rate)} subtitle="non-actionable queries" />
      </div>

      {/* Intent Table */}
      <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden mb-6">
        <div className="px-4 py-3 border-b border-gray-700">
          <h2 className="text-sm font-semibold text-white">Quality by Intent</h2>
        </div>
        <table className="w-full">
          <thead>
            <tr className="text-xs text-gray-500 border-b border-gray-700">
              <th className="text-left px-4 py-2 font-medium">Intent</th>
              <th className="text-right px-4 py-2 font-medium">Queries</th>
              <th className="text-right px-4 py-2 font-medium">Confirmed</th>
              <th className="text-right px-4 py-2 font-medium">Cancelled</th>
              <th className="text-right px-4 py-2 font-medium">Confirm Rate</th>
              <th className="text-right px-4 py-2 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {intents.sort((a, b) => parseFloat(a.confirm_rate || 100) - parseFloat(b.confirm_rate || 100)).map(row => (
              <tr key={row.intent} className="border-b border-gray-700/50 hover:bg-gray-750">
                <td className="px-4 py-2 text-sm text-white font-mono">{row.intent || 'Unknown'}</td>
                <td className="px-4 py-2 text-sm text-gray-300 text-right">{row.total}</td>
                <td className="px-4 py-2 text-sm text-green-400 text-right">{row.confirmed}</td>
                <td className="px-4 py-2 text-sm text-red-400 text-right">{row.cancelled}</td>
                <td className="px-4 py-2 text-sm text-right font-semibold" style={{ color: rateColor(parseFloat(row.confirm_rate)) }}>
                  {row.confirm_rate !== null ? `${row.confirm_rate}%` : '—'}
                </td>
                <td className="px-4 py-2 text-right">
                  {parseFloat(row.confirm_rate || 100) < 80 && (
                    <a href="/admin/knowledge-base" className="text-[10px] text-purple-400 hover:text-purple-300 underline">Write SOP</a>
                  )}
                </td>
              </tr>
            ))}
            {intents.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-sm text-gray-500">No data yet</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Two panel row */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        {/* Corrected Fields */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
          <h2 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-400" />
            Most Corrected Fields
          </h2>
          {fields.length > 0 ? (
            <div className="space-y-2">
              {fields.map((f, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-xs text-gray-300 w-32 truncate">{f.field_name || 'Unknown'}</span>
                  <div className="flex-1 bg-gray-700 rounded-full h-2">
                    <div className="h-2 rounded-full" style={{
                      width: `${(parseInt(f.correction_count) / maxFieldCount) * 100}%`,
                      background: 'linear-gradient(135deg, #c9a84c, #a67c32)',
                    }} />
                  </div>
                  <span className="text-xs text-gray-400 w-8 text-right">{f.correction_count}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-500 py-4 text-center">No corrections recorded</p>
          )}
        </div>

        {/* Weekly Trend — CSS bar chart */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
          <h2 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-green-400" />
            Weekly Confirm Rate (12 weeks)
          </h2>
          {weeks.length >= 2 ? (
            <div className="relative h-36">
              <svg viewBox={`0 0 ${weeks.length * 50} 120`} className="w-full h-full" preserveAspectRatio="none">
                {/* Grid lines */}
                {[0, 25, 50, 75, 100].map(pct => (
                  <line key={pct} x1="0" y1={120 - pct * 1.1} x2={weeks.length * 50} y2={120 - pct * 1.1}
                    stroke="#2a2a35" strokeWidth="0.5" />
                ))}
                {/* Line path */}
                <polyline fill="none" stroke="#c9a84c" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round"
                  points={weeks.map((w, i) => {
                    const conf = parseInt(w.confirmed || 0);
                    const total = conf + parseInt(w.cancelled || 0);
                    const rate = total > 0 ? (conf / total) * 100 : 0;
                    return `${i * 50 + 25},${120 - rate * 1.1}`;
                  }).join(' ')} />
                {/* Dots */}
                {weeks.map((w, i) => {
                  const conf = parseInt(w.confirmed || 0);
                  const total = conf + parseInt(w.cancelled || 0);
                  const rate = total > 0 ? (conf / total) * 100 : 0;
                  return <circle key={i} cx={i * 50 + 25} cy={120 - rate * 1.1} r="3" fill="#c9a84c">
                    <title>{new Date(w.week).toLocaleDateString()}: {Math.round(rate)}%</title>
                  </circle>;
                })}
              </svg>
              <div className="flex justify-between mt-1 px-1">
                {weeks.map((w, i) => (
                  <span key={i} className="text-[8px] text-gray-600">{new Date(w.week).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })}</span>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-xs text-gray-500 py-4 text-center">Not enough data yet</p>
          )}
        </div>
      </div>

      {/* Pinned Stats */}
      {pinnedStats && (parseInt(pinnedStats.auto_approved || 0) + parseInt(pinnedStats.queued || 0) + parseInt(pinnedStats.blocked || 0)) > 0 && (
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
          <h2 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <Pin className="h-4 w-4 text-purple-400" />
            Pinned Answers Funnel
          </h2>
          <div className="flex gap-4">
            {[
              { label: 'Auto-Approved', value: pinnedStats.auto_approved, color: '#16a34a' },
              { label: 'Queued', value: pinnedStats.queued, color: '#c9a84c' },
              { label: 'Blocked', value: pinnedStats.blocked, color: '#dc2626' },
              { label: 'Rejected', value: pinnedStats.rejected, color: '#6b7280' },
              { label: 'Embedded', value: pinnedStats.embedded, color: '#8b5cf6' },
            ].map(s => (
              <div key={s.label} className="flex-1 text-center">
                <div className="text-lg font-bold" style={{ color: s.color }}>{s.value || 0}</div>
                <div className="text-[10px] text-gray-500">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
